import React, { useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { cn } from "../../lib/utils";
import { Button } from "../ui/button";
import { UsageCounter } from "./UsageCounter";
import { usePlan } from "../../contexts/PlanContext";
import { useTranslation } from "react-i18next";
import {
    LayoutDashboard,
    Users,
    FolderKanban,
    FileText,
    Receipt,
    UserCog,
    Bell,
    Settings,
    Sparkles,
    ChevronLeft,
    ChevronRight,
    Building2,
    Clock,
    CalendarDays,
    UsersRound,
    CreditCard,
    Lock,
    FolderOpen,
    MessageSquare,
} from "lucide-react";

// requiredFeature: if set, nav item is locked when plan doesn't include it
// Note: Pro plan has "Finances", Basic has "Factures" - check both for quotes/invoices
const navItems = [
    { path: "/dashboard", labelKey: "nav.dashboard", icon: LayoutDashboard },
    { path: "/clients", labelKey: "nav.clients", icon: Users, requiredFeature: "CRM" },
    { path: "/projects", labelKey: "nav.projects", icon: FolderKanban, requiredFeature: "Projets" },
    { path: "/quotes", labelKey: "nav.quotes", icon: FileText, requiredFeatures: ["Factures", "Finances"] },
    { path: "/invoices", labelKey: "nav.invoices", icon: Receipt, requiredFeatures: ["Factures", "Finances"] },
    { path: "/documents", labelKey: "nav.documents", icon: FolderOpen, requiredFeatures: ["Documents"] },
    { path: "/calendar", labelKey: "nav.calendar", icon: CalendarDays, requiredFeature: "Calendrier" },
    { path: "/messaging", labelKey: "nav.messaging", icon: MessageSquare },
    { path: "/team", labelKey: "nav.team", icon: UserCog },
    { path: "/users", labelKey: "nav.users", icon: UsersRound },
    { path: "/time-tracking", labelKey: "nav.timeTracking", icon: Clock },
    { path: "/ai-assistant", labelKey: "nav.aiAssistant", icon: Sparkles, requiredFeature: "IA" },
    { path: "/notifications", labelKey: "nav.notifications", icon: Bell },
    { path: "/settings/subscription", labelKey: "nav.subscription", icon: CreditCard },
    { path: "/settings", labelKey: "nav.settings", icon: Settings },
];

export const Sidebar = () => {
    const [collapsed, setCollapsed] = useState(false);
    const location = useLocation();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { hasFeature, planData } = usePlan();

    return (
        <aside
            className={cn(
                "fixed left-0 top-0 z-40 h-screen bg-card border-r border-border transition-all duration-300 flex flex-col",
                collapsed ? "w-[72px]" : "w-64"
            )}
            data-testid="sidebar"
        >
            {/* Logo */}
            <div className="flex h-16 items-center justify-between px-4 border-b border-border flex-shrink-0">
                <div className="flex items-center gap-3">
                    <img src="/orkestria-logo.png" alt="Orkestria" className="w-9 h-9 rounded-lg object-contain" />
                    {!collapsed && (
                        <div className="animate-fade-in">
                            <h1 className="font-heading font-bold text-lg text-foreground">Orkestria</h1>
                            <p className="text-[10px] text-muted-foreground">Gestion de Projets</p>
                        </div>
                    )}
                </div>
            </div>

            {/* Navigation */}
            <nav className="p-3 space-y-1 overflow-y-auto flex-1">
                {navItems.map((item) => {
                    const Icon = item.icon;
                    const isActive = item.path === "/settings"
                        ? location.pathname === "/settings"
                        : location.pathname === item.path || location.pathname.startsWith(item.path + "/");

                    // Check feature lock - supports single requiredFeature OR array of requiredFeatures (any match)
                    let featureLocked = false;
                    if (planData) {
                        if (item.requiredFeature) {
                            featureLocked = !hasFeature(item.requiredFeature);
                        } else if (item.requiredFeatures) {
                            // Feature unlocked if user has ANY of the required features
                            featureLocked = !item.requiredFeatures.some(f => hasFeature(f));
                        }
                    }

                    if (featureLocked) {
                        return (
                            <button
                                key={item.path}
                                onClick={() => navigate("/upgrade")}
                                data-testid={`nav-${item.path.slice(1)}-locked`}
                                className={cn(
                                    "flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 w-full text-left",
                                    "text-muted-foreground/50 hover:bg-secondary/50 cursor-not-allowed"
                                )}
                            >
                                <Icon className="w-5 h-5 flex-shrink-0 opacity-40" />
                                {!collapsed && (
                                    <span className="animate-fade-in flex items-center gap-2 opacity-50">
                                        {t(item.labelKey)}
                                        <Lock className="w-3 h-3" />
                                    </span>
                                )}
                            </button>
                        );
                    }

                    return (
                        <NavLink
                            key={item.path}
                            to={item.path}
                            data-testid={`nav-${item.path.slice(1)}`}
                            className={cn(
                                "flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200",
                                isActive
                                    ? "bg-primary text-primary-foreground shadow-sm"
                                    : "text-muted-foreground hover:bg-secondary hover:text-foreground"
                            )}
                        >
                            <Icon className={cn("w-5 h-5 flex-shrink-0", isActive && "text-primary-foreground")} />
                            {!collapsed && <span className="animate-fade-in">{t(item.labelKey)}</span>}
                        </NavLink>
                    );
                })}
            </nav>

            {/* Usage Counter */}
            <div className="flex-shrink-0 pb-2">
                <UsageCounter collapsed={collapsed} />
            </div>

            {/* Collapse Toggle */}
            <div className="flex-shrink-0 p-3 border-t border-border">
                <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setCollapsed(!collapsed)}
                    className="w-full justify-center"
                    data-testid="sidebar-toggle"
                >
                    {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
                </Button>
            </div>
        </aside>
    );
};
